/**
 * メール変更の SSO 再認証（redirect / LINE）中に、変更先メールを一時保存する。
 * sessionStorage。TTL 切れ・uid 不一致は無効。
 */
import type { LastLoginProvider } from '@/utils/auth/lastLoginAccount'

const EMAIL_CHANGE_PENDING_KEY = 'yagish_email_change_pending'
const EMAIL_CHANGE_MISMATCH_KEY = 'yagish_email_change_account_mismatch'
export const EMAIL_CHANGE_PENDING_TTL_MS = 15 * 60 * 1000

export type EmailChangePending = {
  newEmail: string
  uid: string
  provider: LastLoginProvider
  startedAt: number
}

function safeGet(key: string): string | null {
  try {
    return window.sessionStorage.getItem(key)
  } catch (e) {
    return null
  }
}

function safeSet(key: string, value: string): void {
  try {
    window.sessionStorage.setItem(key, value)
  } catch (e) {
    /* 保存できない環境では redirect 復帰できないだけ */
  }
}

function safeRemove(key: string): void {
  try {
    window.sessionStorage.removeItem(key)
  } catch (e) {
    /* noop */
  }
}

function isPendingShape(value: unknown): value is EmailChangePending {
  if (!value || typeof value !== 'object') return false
  const v = value as Record<string, unknown>
  return (
    typeof v.newEmail === 'string' &&
    typeof v.uid === 'string' &&
    typeof v.provider === 'string' &&
    typeof v.startedAt === 'number'
  )
}

export function readEmailChangePending(
  now: number = Date.now()
): EmailChangePending | null {
  const raw = safeGet(EMAIL_CHANGE_PENDING_KEY)
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw)
    if (!isPendingShape(parsed)) {
      clearEmailChangePending()
      return null
    }
    if (now - parsed.startedAt > EMAIL_CHANGE_PENDING_TTL_MS) {
      clearEmailChangePending()
      return null
    }
    return {
      newEmail: parsed.newEmail,
      uid: parsed.uid,
      provider: parsed.provider,
      startedAt: parsed.startedAt
    }
  } catch (e) {
    clearEmailChangePending()
    return null
  }
}

export function writeEmailChangePending(pending: EmailChangePending): void {
  safeSet(EMAIL_CHANGE_PENDING_KEY, JSON.stringify(pending))
}

export function clearEmailChangePending(): void {
  safeRemove(EMAIL_CHANGE_PENDING_KEY)
}

export function writeEmailChangeAccountMismatch(): void {
  safeSet(EMAIL_CHANGE_MISMATCH_KEY, '1')
}

/** 読んで消す。別アカウントで LINE 再認証したとき用 */
export function consumeEmailChangeAccountMismatch(): boolean {
  const raw = safeGet(EMAIL_CHANGE_MISMATCH_KEY)
  safeRemove(EMAIL_CHANGE_MISMATCH_KEY)
  return raw === '1'
}
